/**
 * File system access.
 *
 * The File System Access API gives a real "Save" (write back to the same file
 * the user chose) instead of a download that piles up copies in ~/Downloads.
 * Where it exists we use it; where it does not — Firefox, Safari, and any
 * browser with the API disabled — everything still works through a download and
 * an `<input type=file>`. Nothing in the DAW depends on the newer API being
 * present, which is checked at runtime rather than assumed.
 */

interface FileSystemWritable {
  write(data: BufferSource | Blob | string): Promise<void>;
  close(): Promise<void>;
}

interface FileHandleLike {
  readonly name: string;
  getFile(): Promise<File>;
  createWritable(): Promise<FileSystemWritable>;
}

interface SavePickerOptions {
  suggestedName?: string;
  types?: { description: string; accept: Record<string, string[]> }[];
}

interface OpenPickerOptions extends SavePickerOptions {
  multiple?: boolean;
}

type Picker = {
  showSaveFilePicker?: (options: SavePickerOptions) => Promise<FileHandleLike>;
  showOpenFilePicker?: (options: OpenPickerOptions) => Promise<FileHandleLike[]>;
};

function picker(): Picker {
  return globalThis as unknown as Picker;
}

export const supportsFileSystemAccess = (): boolean =>
  typeof picker().showSaveFilePicker === 'function' && typeof picker().showOpenFilePicker === 'function';

export class FileCancelled extends Error {
  constructor() {
    super('The file dialog was dismissed.');
    this.name = 'FileCancelled';
  }
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export interface SaveTarget {
  /** Present only when the platform supports writing back to the same file. */
  handle: FileHandleLike | null;
  name: string;
}

export async function pickSaveTarget(
  suggestedName: string,
  description: string,
  accept: Record<string, string[]>,
): Promise<SaveTarget> {
  const api = picker().showSaveFilePicker;
  if (!api) return { handle: null, name: suggestedName };
  try {
    const handle = await api({ suggestedName, types: [{ description, accept }] });
    return { handle, name: handle.name };
  } catch (error) {
    if (isAbort(error)) throw new FileCancelled();
    return { handle: null, name: suggestedName };
  }
}

/** Writes bytes to a chosen handle, or falls back to a browser download. */
export async function writeFile(target: SaveTarget, data: Blob): Promise<void> {
  if (target.handle) {
    const writable = await target.handle.createWritable();
    await writable.write(data);
    await writable.close();
    return;
  }
  downloadBlob(data, target.name);
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export async function pickFiles(
  description: string,
  accept: Record<string, string[]>,
  multiple = true,
): Promise<File[]> {
  const api = picker().showOpenFilePicker;
  if (api) {
    try {
      const handles = await api({ multiple, types: [{ description, accept }] });
      return await Promise.all(handles.map((h) => h.getFile()));
    } catch (error) {
      if (isAbort(error)) throw new FileCancelled();
      // Fall through to the input-element path on any other failure.
    }
  }

  return new Promise<File[]>((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = multiple;
    input.accept = Object.values(accept).flat().join(',');
    input.style.display = 'none';
    document.body.appendChild(input);
    let settled = false;
    const finish = (files: File[]): void => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(files);
    };
    input.onchange = () => finish([...(input.files ?? [])]);
    // There is no reliable "cancelled" event for file inputs; a focus-based
    // fallback keeps the promise from leaking if the dialog is dismissed.
    window.addEventListener(
      'focus',
      () => setTimeout(() => finish([...(input.files ?? [])]), 400),
      { once: true },
    );
    input.click();
  });
}

export const AUDIO_ACCEPT: Record<string, string[]> = {
  'audio/wav': ['.wav'],
  'audio/mpeg': ['.mp3'],
  'audio/mp4': ['.m4a', '.aac'],
  'audio/ogg': ['.ogg', '.oga'],
  'audio/flac': ['.flac'],
};

export const PROJECT_ACCEPT: Record<string, string[]> = {
  'application/zip': ['.dawproject'],
};

export const MIDI_ACCEPT: Record<string, string[]> = {
  'audio/midi': ['.mid', '.midi'],
};
